import { EventEmitter } from '@angular/core'
import { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { normalizeNodeId } from './editor-citations-utils'

type CallSentenceCitationSpec = {
  type: 'sentence'
  chunkId: string
  duplicateChunkIds: string[]
  startSentenceId: string
  endSentenceId: string
}

type CallResourceCitationSpec = {
  type: 'resource'
  chunkId: string
  duplicateChunkIds: string[]
  resourceId: number
  resourcePageNumber: number
  resourceName: string
}

type CitationSpec = CallSentenceCitationSpec | CallResourceCitationSpec

type CitationSpecWithContext = {
  citationGroupElement: Element
  citation: CitationSpec
  label: number
}

type EventEmitters = {
  openDocument: EventEmitter<{ resourceId: number; resourcePageNumber: number }>
  openMeeting: EventEmitter<void>
}

type ResourceInfo = {
  id: number
  fileName: string
}

export function createCitationGroupSpanElement(): HTMLSpanElement {
  const span = document.createElement('span')

  span.className = 'citation-group mceNonEditable'

  return span
}

export function createCallSentenceCitationSpan({
  editor,
  chunkRefIds,
  eventEmitters,
  startSentenceId,
  endSentenceId,
  label,
}: {
  editor: any
  chunkRefIds: string[]
  eventEmitters: EventEmitters
  startSentenceId: string
  endSentenceId: string
  label: number
}): HTMLSpanElement {
  const element = document.createElement('span')

  element.className = 'citation-group-item citation-call-sentence mceNonEditable'

  element.setAttribute('data-start-sentence-id', startSentenceId)
  element.setAttribute('data-end-sentence-id', endSentenceId)

  element.textContent = label.toString()

  const chunkRefElements = chunkRefIds.map((chunkRefId) => editor.dom.select(`[data-chunk-id="${chunkRefId}"]`)[0]).filter(Boolean)

  element.addEventListener('mouseenter', () => {
    chunkRefElements.forEach((chunkElement) => chunkElement.classList.add('citation-highlight', 'citation-call-sentence-highlight'))
  })

  element.addEventListener('mouseleave', () => {
    chunkRefElements.forEach((chunkElement) => chunkElement.classList.remove('citation-highlight', 'citation-call-sentence-highlight'))
  })

  element.addEventListener('click', () => {
    // Sets the cursor to the beginning of the referenced chunk.
    editor.selection.setCursorLocation(editor.dom.select(`[data-chunk-id="${chunkRefIds[0]}"]`)[0], 0)

    eventEmitters.openMeeting.emit()
  })

  return element
}

export function createCallResourceCitationSpan({
  editor,
  chunkRefIds,
  eventEmitters,
  resourceId,
  resourcePageNumber,
  resourceName,
  formatPageLabel,
  label,
}: {
  editor: any
  chunkRefIds: string[]
  eventEmitters: EventEmitters
  resourceId: number
  resourcePageNumber: number
  resourceName: string
  formatPageLabel: (pageNumber: number) => string
  label: number
}): HTMLSpanElement {
  const element = document.createElement('span')

  element.className = 'citation-group-item citation-call-resource mceNonEditable'

  element.setAttribute('data-call-resource-id', resourceId.toString())
  element.setAttribute('data-call-resource-page-number', resourcePageNumber.toString())

  element.textContent = label.toString()

  // Create tooltip element for file name and page number
  const tooltip = document.createElement('div')

  tooltip.className = 'citation-tooltip'
  tooltip.textContent = `${resourceName} (${formatPageLabel(resourcePageNumber)})`

  const chunkRefElements = chunkRefIds.map((chunkRefId) => editor.dom.select(`[data-chunk-id="${chunkRefId}"]`)[0]).filter(Boolean)

  element.addEventListener('mouseenter', () => {
    chunkRefElements.forEach((chunkElement) => chunkElement.classList.add('citation-highlight', 'citation-call-resource-highlight'))

    // Add tooltip to editor body so it can be positioned correctly
    const editorBody = editor.getBody()
    editorBody.appendChild(tooltip)

    // Get element position relative to the document
    const elementRect = element.getBoundingClientRect()
    const editorBodyRect = editorBody.getBoundingClientRect()

    // Calculate position: below the citation element
    let left = elementRect.left - editorBodyRect.left + editorBody.scrollLeft
    const top = elementRect.bottom - editorBodyRect.top + editorBody.scrollTop + 4

    // Apply initial position to measure tooltip width
    tooltip.style.left = `${left}px`
    tooltip.style.top = `${top}px`

    // Check if tooltip would overflow on the right side and adjust
    const tooltipWidth = tooltip.offsetWidth
    const editorWidth = editorBody.clientWidth
    if (left + tooltipWidth > editorWidth - 10) {
      left = editorWidth - tooltipWidth - 10
    }

    // Ensure tooltip doesn't go past the left edge
    if (left < 10) {
      left = 10
    }

    tooltip.style.left = `${left}px`
    tooltip.classList.add('visible')
  })

  element.addEventListener('mouseleave', () => {
    chunkRefElements.forEach((chunkElement) => chunkElement.classList.remove('citation-highlight', 'citation-call-resource-highlight'))

    if (tooltip.parentNode !== null) {
      tooltip.classList.remove('visible')
      tooltip.parentNode.removeChild(tooltip)
    }
  })

  element.addEventListener('click', () => {
    // Sets the cursor to the beginning of the referenced chunk.
    editor.selection.setCursorLocation(editor.dom.select(`[data-chunk-id="${chunkRefIds[0]}"]`)[0], 0)

    eventEmitters.openDocument.emit({
      resourceId,
      resourcePageNumber,
    })
  })

  return element
}

export function containsCitations(editor: any): boolean {
  return editor.dom.select('span.citation-group').length > 0
}

export function insertCitationTags({
  editor,
  citations,
  eventEmitters,
  resources,
  formatPageLabel,
}: {
  editor: any
  citations: CitationMinuteNodes
  eventEmitters: EventEmitters
  resources: ResourceInfo[]
  formatPageLabel: (pageNumber: number) => string
}) {
  if (containsCitations(editor)) {
    // TODO: This is a workaround for issue that the citation tags are getting saved as a revision by the editor.
    //
    // When you switch from the minutes to outline tab and back, it reloads the editor with the latest revision which
    // already has the citation tags, but since these are saved they lose the mouse and click event handlers that we
    // attached in order for a lot of the citation functionality to work.
    //
    // For now removing the existing citation tags and re-inserting them works, but we can get rid of this once I can
    // figure out how to get the editor to not save these citation tags as part of the it's revision management.
    // - Geraint
    editor.dom.remove(editor.dom.select('span.citation-group'))
  }

  // Inline helper: Find or create citation group element for a parent element
  const getOrCreateCitationGroup = (parentElement: HTMLElement): Element => {
    const existing = parentElement.querySelector('.citation-group')

    if (existing !== null) {
      return existing
    }

    const newGroup = createCitationGroupSpanElement()

    parentElement.appendChild(newGroup)

    return newGroup
  }

  // Inline helper: Convert chunk citations to citation specs
  const chunkToCitationSpecs = (chunkId: string, chunk: { callSentenceCitations: any[]; callResourceCitations: any[] }): CitationSpec[] => {
    const sentenceSpecs: CitationSpec[] = chunk.callSentenceCitations.map((citation) => ({
      type: 'sentence' as const,
      chunkId,
      duplicateChunkIds: citation.duplicateChunkIds,
      startSentenceId: citation.startSentenceId,
      endSentenceId: citation.endSentenceId,
    }))

    const resourceSpecs: CitationSpec[] = chunk.callResourceCitations
      .map((citation) => {
        const resource = resources.find((resource) => resource.id === citation.resourceId) ?? null

        if (resource === null) {
          console.warn(`Could not find resource with ID ${citation.resourceId} for call resource in chunk with ID ${chunkId}.`)

          return null
        }

        return {
          type: 'resource' as const,
          chunkId,
          duplicateChunkIds: citation.duplicateChunkIds,
          resourceId: citation.resourceId,
          resourcePageNumber: citation.resourcePageNumber,
          resourceName: resource.fileName,
        }
      })
      .filter((spec): spec is CallResourceCitationSpec => spec !== null)

    return [...sentenceSpecs, ...resourceSpecs]
  }

  // Transform citations into a flat array of citation specs with labels
  // Uses reduce to maintain label index state that resets per parent node
  const citationSpecsWithContext: CitationSpecWithContext[] = Object.entries(citations).flatMap(
    ([parentNodeId, citationMinuteNode]) =>
      Object.entries(citationMinuteNode.items).reduce<{ specs: CitationSpecWithContext[]; labelIndex: number }>(
        (result, [rawNodeId, { chunks }]) => {
          const nodeId = normalizeNodeId(rawNodeId)
          const parentElementQuery = `[data-parent-node-id="${parentNodeId}"][data-node-id="${nodeId}"]`
          const parentElement: HTMLElement | null = editor.dom.select(parentElementQuery)[0] ?? null

          if (parentElement === null) {
            console.info(`Could not find parent element with query selector: '${parentElementQuery}', it might have been removed from the minutes.`)

            return result
          }

          const citationGroupElement = getOrCreateCitationGroup(parentElement)

          const chunkSpecs = Object.entries(chunks).flatMap(([chunkId, chunk]) => {
            const chunkExists = parentElement.querySelector(`[data-chunk-id="${chunkId}"]`) !== null

            if (!chunkExists) {
              console.info(`Could not find chunk element with ID '${chunkId}', it might have been removed from the minutes.`)

              return []
            }

            return chunkToCitationSpecs(chunkId, chunk)
          })

          const specsWithLabels = chunkSpecs.map((citation, index) => ({
            citationGroupElement,
            citation,
            label: result.labelIndex + index,
          }))

          return {
            specs: [...result.specs, ...specsWithLabels],
            labelIndex: result.labelIndex + chunkSpecs.length,
          }
        },
        { specs: [], labelIndex: 1 }
      ).specs
  )

  citationSpecsWithContext.forEach(({ citationGroupElement, citation, label }) => {
    const element =
      citation.type === 'sentence'
        ? createCallSentenceCitationSpan({
            editor,
            chunkRefIds: [citation.chunkId, ...citation.duplicateChunkIds],
            eventEmitters,
            startSentenceId: citation.startSentenceId,
            endSentenceId: citation.endSentenceId,
            label,
          })
        : createCallResourceCitationSpan({
            editor,
            chunkRefIds: [citation.chunkId, ...citation.duplicateChunkIds],
            eventEmitters,
            resourceId: citation.resourceId,
            resourcePageNumber: citation.resourcePageNumber,
            resourceName: citation.resourceName,
            formatPageLabel,
            label,
          })

    citationGroupElement.appendChild(element)
  })
}

export function initCitations({
  editor,
  citations,
  eventEmitters,
  resources,
  formatPageLabel,
}: {
  editor: any
  citations: CitationMinuteNodes
  eventEmitters: EventEmitters
  resources: ResourceInfo[]
  formatPageLabel: (pageNumber: number) => string
}) {
  editor.on('SetContent', () => {
    insertCitationTags({ editor, citations, eventEmitters, resources, formatPageLabel })
  })

  insertCitationTags({ editor, citations, eventEmitters, resources, formatPageLabel })
}

export function getContentWithStrippedCitationsFromEditor({
  editor,
  citations,
  eventEmitters,
  resources,
  formatPageLabel,
}: {
  editor: any
  citations: CitationMinuteNodes | null
  eventEmitters: EventEmitters
  resources: ResourceInfo[]
  formatPageLabel: (pageNumber: number) => string
}): string {
  if (citations === null) {
    return editor.getContent()
  }

  editor.dom.remove([...editor.dom.select('span.citation-group'), ...editor.dom.select('.citation-tooltip')])

  const content = editor.getContent()

  insertCitationTags({ editor, citations, eventEmitters, resources, formatPageLabel })

  return content
}
